/**
 * AES-GCM Encryption/Decryption (Web Crypto API)
 * Symmetric encryption for recipient passwords
 *
 * Security Parameters:
 * - Algorithm: AES-256-GCM
 * - Key Size: 256 bits
 * - Nonce (IV): 12 bytes (96 bits, recommended for GCM)
 * - Authentication Tag: 128 bits (included in ciphertext)
 *
 * Use Cases:
 * - Encrypting recipient emails with MasterKey
 * - Encrypting recipient passwords with MasterKey
 * - Each record has unique IV (stored in EmpfaengerPasswort.IV)
 *
 * GCM Benefits:
 * - Authenticated encryption (integrity + confidentiality)
 * - Parallelizable (faster than CBC)
 * - No padding required
 * - Authentication tag prevents tampering
 */

const AES_KEY_LENGTH = 256; // bits
const NONCE_LENGTH = 12; // bytes (96 bits, recommended for GCM)

/**
 * Generates random MasterKey for password database
 *
 * @returns {Uint8Array} - Random 32-byte key
 */
export function generateMasterKey() {
  return window.crypto.getRandomValues(new Uint8Array(32)); // 256 bits
}

/**
 * Generates random nonce (IV) for AES-GCM
 *
 * @returns {Uint8Array} - Random 12-byte nonce
 */
export function generateNonce() {
  return window.crypto.getRandomValues(new Uint8Array(NONCE_LENGTH));
}

/**
 * Encrypts text with MasterKey (AES-256-GCM)
 *
 * Flow:
 * 1. Import MasterKey as AES-GCM CryptoKey
 * 2. Generate random nonce
 * 3. Encrypt plaintext with AES-GCM
 * 4. Return ciphertext + nonce
 *
 * @param {string} plaintext - Text to encrypt (email or password)
 * @param {Uint8Array} masterKeyBytes - MasterKey (32 bytes)
 * @returns {Promise<{ciphertext: string, nonce: string}>} - Base64 encoded
 */
export async function encryptWithMasterKey(plaintext, masterKeyBytes) {
  try {
    // Step 1: Import MasterKey as CryptoKey
    const aesKey = await window.crypto.subtle.importKey(
      "raw",
      masterKeyBytes,
      { name: "AES-GCM", length: AES_KEY_LENGTH },
      false,
      ["encrypt"]
    );

    // Step 2: Generate random nonce
    const nonce = generateNonce();

    // Step 3: Encrypt plaintext
    const encoder = new TextEncoder();
    const plaintextBuffer = encoder.encode(plaintext);

    const encryptedBuffer = await window.crypto.subtle.encrypt(
      {
        name: "AES-GCM",
        iv: nonce,
      },
      aesKey,
      plaintextBuffer
    );

    // Step 4: Return Base64 encoded
    return {
      ciphertext: arrayBufferToBase64(new Uint8Array(encryptedBuffer)),
      nonce: arrayBufferToBase64(nonce),
    };
  } catch (error) {
    console.error("AES-GCM encryption failed:", error);
    throw new Error("Failed to encrypt data");
  }
}

/**
 * Encrypts text with MasterKey using provided nonce (AES-256-GCM)
 *
 * Same as encryptWithMasterKey but uses provided nonce instead of generating new one.
 * Used for encrypting multiple fields with same IV (backend compatibility).
 *
 * @param {string} plaintext - Text to encrypt (email or password)
 * @param {Uint8Array} masterKeyBytes - MasterKey (32 bytes)
 * @param {Uint8Array} nonce - Pre-generated nonce (12 bytes)
 * @returns {Promise<{ciphertext: string}>} - Base64 encoded ciphertext only
 */
export async function encryptWithMasterKeyWithNonce(plaintext, masterKeyBytes, nonce) {
  try {
    // Step 1: Import MasterKey as CryptoKey
    const aesKey = await window.crypto.subtle.importKey(
      "raw",
      masterKeyBytes,
      { name: "AES-GCM", length: AES_KEY_LENGTH },
      false,
      ["encrypt"]
    );

    // Step 2: Encrypt plaintext with provided nonce
    const encoder = new TextEncoder();
    const plaintextBuffer = encoder.encode(plaintext);

    const encryptedBuffer = await window.crypto.subtle.encrypt(
      {
        name: "AES-GCM",
        iv: nonce,
      },
      aesKey,
      plaintextBuffer
    );

    // Step 3: Return ciphertext only (nonce is shared)
    return {
      ciphertext: arrayBufferToBase64(new Uint8Array(encryptedBuffer)),
    };
  } catch (error) {
    console.error("AES-GCM encryption with nonce failed:", error);
    throw new Error("Failed to encrypt data");
  }
}

/**
 * Decrypts ciphertext with MasterKey (AES-256-GCM)
 *
 * Flow:
 * 1. Import MasterKey as AES-GCM CryptoKey
 * 2. Decrypt ciphertext with nonce
 * 3. Return plaintext string
 *
 * @param {string} ciphertextBase64 - Encrypted data (Base64)
 * @param {string} nonceBase64 - Nonce/IV (Base64)
 * @param {Uint8Array} masterKeyBytes - MasterKey (32 bytes)
 * @returns {Promise<string>} - Decrypted plaintext
 */
export async function decryptWithMasterKey(
  ciphertextBase64,
  nonceBase64,
  masterKeyBytes
) {
  try {
    // Step 1: Import MasterKey as CryptoKey
    const aesKey = await window.crypto.subtle.importKey(
      "raw",
      masterKeyBytes,
      { name: "AES-GCM", length: AES_KEY_LENGTH },
      false,
      ["decrypt"]
    );

    // Step 2: Decrypt ciphertext
    const ciphertext = base64ToArrayBuffer(ciphertextBase64);
    const nonce = base64ToArrayBuffer(nonceBase64);

    const decryptedBuffer = await window.crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv: nonce,
      },
      aesKey,
      ciphertext
    );

    // Step 3: Convert to string
    const decoder = new TextDecoder();
    return decoder.decode(decryptedBuffer);
  } catch (error) {
    console.error("AES-GCM decryption failed:", error);
    throw new Error("Failed to decrypt data (incorrect key or corrupted data)");
  }
}

/**
 * Encrypts recipient password entry
 *
 * Uses SINGLE shared IV for both email and password (backend compatibility).
 * Backend schema: EmpfaengerPasswort has one IV field for both encrypted fields.
 *
 * Security: Safe because each record gets unique IV, plaintexts are different.
 * GCM requirement: (Key + IV) combination must not repeat → satisfied by unique IV per record.
 *
 * @param {string} recipientEmail - Recipient email address
 * @param {string} password - Recipient password
 * @param {Uint8Array} masterKeyBytes - MasterKey (32 bytes)
 * @returns {Promise<{encryptedEmail: string, encryptedPassword: string, iv: string}>}
 */
export async function encryptRecipientPassword(
  recipientEmail,
  password,
  masterKeyBytes
) {
  try {
    // Generate single shared IV for this record
    const sharedNonce = generateNonce();

    // Encrypt email with shared IV
    const emailResult = await encryptWithMasterKeyWithNonce(
      recipientEmail,
      masterKeyBytes,
      sharedNonce
    );

    // Encrypt password with same shared IV
    const passwordResult = await encryptWithMasterKeyWithNonce(
      password,
      masterKeyBytes,
      sharedNonce
    );

    return {
      encryptedEmail: emailResult.ciphertext,
      encryptedPassword: passwordResult.ciphertext,
      iv: arrayBufferToBase64(sharedNonce), // Single IV for both fields
    };
  } catch (error) {
    console.error("Recipient password encryption failed:", error);
    throw new Error("Failed to encrypt recipient password");
  }
}

/**
 * Decrypts recipient password entry
 *
 * Uses SINGLE shared IV for both email and password (backend compatibility).
 *
 * @param {string} encryptedEmail - Encrypted email (Base64)
 * @param {string} encryptedPassword - Encrypted password (Base64)
 * @param {string} iv - Shared IV for both fields (Base64)
 * @param {Uint8Array} masterKeyBytes - MasterKey (32 bytes)
 * @returns {Promise<{email: string, password: string}>}
 */
export async function decryptRecipientPassword(
  encryptedEmail,
  encryptedPassword,
  iv,
  masterKeyBytes
) {
  try {
    // Decrypt email with shared IV
    const email = await decryptWithMasterKey(
      encryptedEmail,
      iv,
      masterKeyBytes
    );

    // Decrypt password with same shared IV
    const password = await decryptWithMasterKey(
      encryptedPassword,
      iv,
      masterKeyBytes
    );

    return { email, password };
  } catch (error) {
    console.error("Recipient password decryption failed:", error);
    throw new Error("Failed to decrypt recipient password");
  }
}

/**
 * Utility: ArrayBuffer to Base64
 */
function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

/**
 * Utility: Base64 to ArrayBuffer
 */
function base64ToArrayBuffer(base64) {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}
